import { Fragment, useEffect, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';
import Button from 'reactstrap/lib/Button';
import Col from 'reactstrap/lib/Col';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import Row from 'reactstrap/lib/Row';
import TabContent from 'reactstrap/lib/TabContent';
import TabPane from 'reactstrap/lib/TabPane';
import { Alert, Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { getIn } from 'formik';
import update from 'immutability-helper';
import { get } from 'lodash';
import queryString from 'query-string';

import { deleteApplication } from 'Lib/api/applications';
import Address from 'Lib/components/common/Address';
import Card from 'Lib/components/common/Card';
import Link from 'Lib/components/common/Link';
import { PageNav } from 'Lib/components/common/Page';
import { RenderIcon } from 'Lib/components/common/RenderIcon';
import { FilesProvider } from 'Lib/components/common/table/filesTable/context';
import FilesTable from 'Lib/components/common/table/filesTable/FilesTable';
import Text from 'Lib/components/common/Text';
import { deleteFileHandler, dropHandler } from 'Lib/components/files/helpers';
import { ShowFiles } from 'Lib/components/files/utils';
import FieldArray from 'Lib/components/form/FieldArray';
import Form from 'Lib/components/form/Form';
import FormPanel from 'Lib/components/form/FormPanel';
import TextField from 'Lib/components/form/TextField';
import withForm from 'Lib/components/hoc/withForm';
import ConfirmDateModal from 'Lib/components/modals/ConfirmDateModal';
import PromptModal from 'Lib/components/modals/PromptModal';
import { default as ApplicationEntryField } from 'Lib/components/screening/application/ApplicationField';
import CancelButton from 'Lib/components/screening/application/components/CancelButton';
import Pay from 'Lib/components/screening/application/modals/Pay';
import ObjectTypes from 'Lib/constants/app/objectTypes';
import PortalTypes from 'Lib/constants/app/portalTypes';
import ApplicationFieldTypes from 'Lib/constants/screening/applicationFieldTypes';
import ApplicationSections from 'Lib/constants/screening/applicationSections';
import ApplicationSteps from 'Lib/constants/screening/applicationSteps';
import ApplicationsContext from 'Lib/contexts/ApplicationsContext';
import useApi from 'Lib/hooks/useApi';
import useNotify from 'Lib/hooks/useNotify';
import usePromptModal from 'Lib/hooks/usePromptModal';
import fileSizeValidator from 'Lib/utilities/fileSizeValidator';
import getAge from 'Lib/utilities/getAge';
import getErrorMessages from 'Lib/utilities/getErrorMessages';
import isEmpty from 'Lib/utilities/isEmpty';
import isNil from 'Lib/utilities/isNil';
import { screenSize } from 'Lib/utilities/screenSize';

import VerificationDocuments from './cards/components/VerificationDocuments';
import IdentityVerification from './cards/IdentityVerification';
import IncomeVerification from './cards/IncomeVerification';
import Submit from './cards/Submit';
import SubmitOnly from './cards/SubmitOnly';
import ApplicationSuccess from './components/ApplicationSuccess';
import PreviewingAlert from './components/PreviewingAlert';
import AddendumModal from './modals/addendum/index';
import IdentityPage from './IdentityPage';
import TransactionsPage from './TransactionsPage';

import './Application.css';

const APPLYING_FOR = 'applyingFor';

const ApplicantTabs = Object.freeze({
	Applicant: 'applicant',
	Files: 'files',
	Addenda: 'addenda',
	Income: 'income',
	Identity: 'identity',
	Landlord: 'landlord'
});

function Application(props) {
	const history = useHistory();
	const { toastError } = useNotify();

	const { applicantID: routeApplicantID } = props.match.params;
	const { generic, application: startedApplicationID, preview, unitID, templateID: previewTemplateID } = queryString.parse(history.location.search);

	const company = useSelector(state => state.app.company);

	const cancelApplication = useApi(deleteApplication);

	// scroll controllers
	const scroller = useRef(null);

	// Components supplied via Props
	const UnitField = props.components.UnitField;
	const Page = props.components.Page;
	const Listings = props.components.Listings;
	const LandlordVerificationPage = props.components.LandlordVerificationPage;
	const RenderAuditLogModal = props.components.RenderAuditLogModal;

	const [confirmModal, setConfirmModal] = useState({
		isOpen: false,
		birthDate: null,
		fieldName: null,
		form: null
	});
	// responsive state
	const [isMobile, setIsMobile] = useState(false);

	const [isLoading, setLoading] = useState(true);
	const [isFileProcessing, setFileProcessing] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);
	const [allowCancel, setAllowCancel] = useState(false);
	const [documentSection, setDocumentSection] = useState({});
	const [initialValues, setInitialValues] = useState({});
	const [sectionInterface, setSectionInterface] = useState({});
	const [steps, setSteps] = useState([]);
	const [currentStep, setCurrentStep] = useState(0);
	const [reviewMessage, setReviewMessage] = useState(null);
	const [paymentMessage, setPaymentMessage] = useState(null);
	const [tab, setTab] = useState('applicant');

	// SSN
	const [isEditingSocial, setSocialEdit] = useState(false);
	const [ssnLoading, setSsnLoading] = useState(false);
	const [ssn, setSsn] = useState(null);

	// Application/Applicant State
	const [isEditing, setEditing] = useState(props.steps);
	const [applicant, setApplicant] = useState({});
	const [appliedToAddress, setAppliedToAddress] = useState({});
	const [applicantID, setApplicantID] = useState(routeApplicantID);
	const [applicationInformation, setApplication] = useState({});
	const [pendingMessage, setPendingMessage] = useState(null);
	const [hasPendingApplication, setHasPendingApplication] = useState(false);
	const [urlUUID, setUrlUUID] = useState(startedApplicationID);
	const [originalStartedApplicationID, setOriginalStartedApplicationID] = useState(null);
	const pendingMessageModal = usePromptModal();
	const [previousApplication, setPreviousApplication] = useState(null);

	// Steps State
	const [isValidating, setValidating] = useState(false);
	const [showSubmitModal, setSubmit] = useState(false);
	const [changingUnit, toggleChange] = useState(false);
	const [selectedUnit, setSelectedUnit] = useState({});
	const [selectedCountryID, setSelectedCountryID] = useState('US');
	const [templateID, setTemplateID] = useState(null);
	const [paymentInformation, setPaymentInformation] = useState({});
	const [stepLabels, setStepLabels] = useState([]);

	// Addenda
	const [addenda, setAddenda] = useState([]);
	const [addendum, setAddendum] = useState(null);
	const [signature, setSignature] = useState(null);
	const [promptSignature, setPromptSignature] = useState(false);

	// File State
	const [fileIDs, setFileIDs] = useState([]);
	const [fileNames, setFileNames] = useState([]);
	const [filePreviews, setFilePreviews] = useState([]);
	const onUploadFile = useApi(props.endpoints.uploadFile);
	const onDelete = useApi(props.endpoints.deleteFile);
	const config = props.config || {};

	// Income Verification State
	const [providerLinkToken, setProviderLinkToken] = useState(null);
	const [incomeVerificationCompleted, setIncomeVerificationCompleted] = useState(false);

	// Identity Verification State
	const [identityProviderToken, setIdentityProviderToken] = useState(null);
	const [identityVerificationCompleted, setIdentityVerificationCompleted] = useState(false);

	// Transactions State
	const [incomeTransactions, setIncomeTransactions] = useState([]);

	// Identity Verification Data State
	const [identityVerificationData, setIdentityVerificationData] = useState([]);

	const [showAuditLog, setShowAuditLog] = useState(false);

	const fetchApplication = useApi(props.endpoints.getApplication);
	const fetchGenericApplication = useApi(props.endpoints.getGenericApplication);
	const fetchBuilder = useApi(props.endpoints.getBuilder);
	const fetchTemplate = useApi(props.endpoints.getTemplateByView);
	const fetchSocial = useApi(props.endpoints.getApplicantSocial);
	const submitSocial = useApi(props.endpoints.saveApplicantSocial);
	const submitApplication = useApi(props.endpoints.submitApplication);
	const updateApplicant = useApi(props.endpoints.updateApplicant);
	const getStartedApplication = useApi(props.endpoints.getStartedApplication);
	const createStartedApplication = useApi(props.endpoints.createStartedApplication);
	const updateStartedApplication = useApi(props.endpoints.updateStartedApplication);
	const fetchIncomeVerificationToken = useApi(props.endpoints.fetchIncomeVerificationToken);
	const setIncomeVerificationToken = useApi(props.endpoints.setIncomeVerificationToken);
	const fetchIdentityVerificationToken = useApi(props.endpoints.fetchIdentityVerificationToken);
	const resendIdentityVerification = useApi(props.endpoints.resendIdentityVerification);
	const getLandlordVerifications = useApi(props.endpoints.getLandlordVerifications);
	const sendLandlordVerification = useApi(props.endpoints.sendLandlordVerification);

	const { mobile } = screenSize();

	const isSelectingListing = !generic && !urlUUID && !props.templateID && !previewTemplateID && isEmpty(selectedUnit) && !props.id;

	useEffect(() => {
		window.addEventListener('resize', isTouchDevice);
		isTouchDevice();
	}, []);

	useEffect(() => {
		if (generic || urlUUID || (!props.steps && props.id)) {
			getApp();
		} else if (unitID) {
			setUnit(unitID);
		} else if (previewTemplateID || props.templateID) {
			getApp();
			setTemplateID(props.templateID || previewTemplateID);
		} else {
			setLoading(false);
		}
	}, [props.id, generic, urlUUID]);

	useEffect(() => {
		setUrlUUID(startedApplicationID);
	}, [startedApplicationID]);

	useEffect(() => {
		if ((!urlUUID && generic) || (!isEmpty(selectedUnit) && !urlUUID)) {
			const payload = { unitID: generic ? null : selectedUnit.unitID };

			createStartedApplication(payload)
				.then(response => {
					setUrlUUID(response.data.urlUUID);
				})
				.catch(error => {
					if (error.response?.data?.hasStartedApplication) {
						pendingMessageModal.close();
						setPreviousApplication(error.response?.data?.applicationID);
					} else {
						props.notify({ error });
					}
				});
		}
	}, [selectedUnit, generic]);

	useEffect(() => {
		const stepID = steps && steps.length && steps[currentStep].id;

		const { sections = [] } = steps[currentStep] ?? {};
		const incomeEnabled = sections.some(section => section.name == 'incomeVerification' && section.isActive);
		const identityEnabled = sections.some(section => section.name == 'identityVerification' && section.isActive);

		if (urlUUID && !providerLinkToken && incomeEnabled && stepID === ApplicationSteps.IncomeVerification) {
			fetchIncomeVerificationToken(urlUUID).then(response => {
				if (response.data.verificationCompleted) {
					setIncomeVerificationCompleted(true);
				} else {
					setProviderLinkToken(response.data.token);
				}
			});
		}

		if (urlUUID && !identityProviderToken && identityEnabled && stepID === ApplicationSteps.IdentityVerification) {
			fetchIdentityVerificationToken(urlUUID).then(response => {
				if (response.data.verificationCompleted) {
					setIdentityVerificationCompleted(true);
				} else {
					setIdentityProviderToken(response.data.token);
				}
			});
		}
	}, [currentStep, urlUUID, steps]);

	useEffect(() => void scrollToTop(), [currentStep]);

	const scrollToTop = () => {
		if (scroller.current && scroller.current.parentNode) {
			scroller.current.parentNode.scrollTo(0, 0);
		}
	};

	const getApp = () => {
		setLoading(true);

		if (generic) {
			fetchGenericApplication()
				.then(response => {
					processSteps({ steps: response.data.template.steps, payment: response.data.template.payment }, {});
					setTemplateID(response.data.template.id);
					setAddenda(response.data.template.addenda);
					setEditing(true);
				})
				.catch(error => {
					props.notify({ error });
					setLoading(false);
				});
		} else if (urlUUID) {
			getStartedApplication(urlUUID, { includes: 'applicant' })
				.then(response => {
					setApplicant(response.data.applicant);

					// If application has already been completed, then load the success page instead
					// of processing the steps
					if (response.data.startedApplication.lastStepID == ApplicationSteps.Pay) {
						setLoading(false);
						setShowSuccess(true);
						setEditing(false);

						return;
					}

					// Address provided in the startedApplication data means that the PM used a manual address
					// when creating the invitation. In this case, we want to use it instead of the unitID to
					// set the address for fields so they are preset with the manual address for the applicant.
					const manualAddress = isEmpty(response.data.startedApplication.unitID) && response.data.startedApplication.unit;
					const unitData = manualAddress ? { unit: manualAddress } : null;

					if (unitData) {
						processSteps(
							{
								steps: response.data.template.steps,
								payment: response.data.template.payment
							},
							{ ...unitData, isGeneric: response.data.startedApplication.isGeneric, template: response.data.template },
							response.data.startedApplication.initialData
						);
					}

					if (!isEmpty(response.data.startedApplication.data)) {
						setApplication(response.data.startedApplication.data);
					} else if (manualAddress) {
						setSelectedUnit(manualAddress);
					}

					setCurrentStep(response.data.startedApplication.lastStepIndex || 0);
					setUnit(response.data.startedApplication.unitID);
					setTemplateID(response.data.template.id);
					setAddenda(response.data.template.addenda);
				})
				.catch(error => {
					setUrlUUID(null);
					props.notify({ error: 'Error. ' + error.response?.data?.error, autoClose: false });
					if (error.response?.data?.allowCancel) {
						setOriginalStartedApplicationID(error.response?.data?.startedApplicationID);
						setAllowCancel(true);
					}
				})
				.finally(() => {
					setLoading(false);
				});
		} else if (props.templateID) {
			fetchTemplate({ templateID: props.templateID })
				.then(response => {
					processSteps({ steps: response.data.template.steps, payment: response.data.payment }, {});
					setLoading(false);
					setEditing(true);
				})
				.catch(error => props.notify({ error }));
		} else if (previewTemplateID) {
			fetchBuilder(previewTemplateID)
				.then(response => {
					processSteps({ steps: response.data.steps, payment: response.data.payment }, {});
					setAddenda(response.data.addenda);
					setApplicantID(previewTemplateID);
					setLoading(false);
				})
				.catch(error => props.notify({ error }));
		} else {
			// Fetch the submitted application that contains information about each applicant and their data
			fetchApplication(routeApplicantID)
				.then(response => {
					processSteps({ steps: response.data.builder.steps, payment: response.data.payment }, {});

					setApplicant(response.data.applicant);

					setApplicantID(routeApplicantID);
					setApplication(response.data.applicantData);
					setAddenda(response.data.addenda);
					setIncomeTransactions(response.data.transactions);
					setIdentityVerificationData(response.data.identityVerification);

					setLoading(false);
				})
				.catch(error => props.notify({ error }));
		}
	};

	const onDeleteApplication = (form, id) => {
		return cancelApplication(form, id).then(() => {
			history.push('/');
		});
	};

	const handleIncomeVerificationToken = token => {
		setIncomeVerificationToken(urlUUID, { token: token })
			.then(() => {
				setIncomeVerificationCompleted(true);
			})
			.catch(error => props.notify({ error }));
	};

	const toggleSignature = () => {
		setPromptSignature(prevState => !prevState);
	};

	const handleIdentityVerificationResubmitted = (data, form) => {
		const applicantID = routeApplicantID;

		form.setSubmitting(true);

		return props.form
			.handlePromise(form, [resendIdentityVerification(applicantID, data)], {
				successMessage: 'Successfully resent identity verification'
			})
			.then(result => {
				setIdentityVerificationData(result.data);
				form.setSubmitting(false);
			})
			.catch(error => {
				props.notify({ error });
				form.setSubmitting?.(false);
				form.setProcessing?.(false);
			});
	};

	const processSteps = ({ steps, payment }, { template, unit, isGeneric }, provided = {}) => {
		const labels = [];
		const initial = {};
		const multiInterface = {};

		const mappedSteps = steps.map(step => {
			labels.push(step.label);

			return {
				...step,
				sections: step.sections.flatMap(section => {
					const fields = {};
					const variableFields = [];
					const count = parseInt(section.requiredCount) >= 1 ? parseInt(section.requiredCount) : 0;
					const isVariableSection = section.id == ApplicationSections.Terms || section.id == ApplicationSections.Questions;

					if (isVariableSection) {
						initial[section.name] = [];
					} else {
						initial[section.name] = {};
					}

					if (section.id == ApplicationSections.Documents) {
						setDocumentSection(section);
					}
					if (section.id == ApplicationSections.ReviewSign) {
						setReviewMessage(section.message);
					}
					if (section.id == ApplicationSections.Pay) {
						setPaymentMessage(section.message);
					}

					section.groups.forEach(group => {
						group.fields.map(field => {
							if (!isGeneric && unit && step.id == ApplicationSteps.ApplyingFor) {
								switch (field.name) {
									case 'countryID':
									case 'address':
									case 'address2':
									case 'stateID':
									case 'city':
									case 'postalCode':
										field['readOnly'] = true;
										break;
									default:
										break;
								}
							}

							if (!isGeneric && ((template?.isLockRentAmount == '1' && field.name == 'rent') || (template?.isLockDepositAmount == '1' && field.name == 'deposit'))) {
								field['readOnly'] = true;
							}

							if (field.variableFieldID) {
								variableFields.push({ variableFieldID: field.variableFieldID });
							} else if (field.name == 'countryID' || field.name == 'primaryCountryID') {
								fields[field.name] = selectedCountryID || 'US';
							} else if (field.name) {
								fields[field.name] = get(provided, `${section.name}.${field.name}`, null);
							}

							return field;
						});
					});

					if (section.isMulti) {
						if (section.id != ApplicationSections.Documents && count >= 0) {
							initial[section.name].items = Array(parseInt(count)).fill(fields);
						}
						multiInterface[section.name] = [fields];
					} else if (isVariableSection) {
						initial[section.name] = variableFields;
					} else {
						if (unit) {
							// Loops through already set values on a Unit (address, postalCode, rent, deposit, etc)
							Object.keys(fields).forEach(key => {
								if (!isNil(unit[key])) {
									fields[key] = unit[key];
								}
							});

							initial[section.name] = fields;
						}
						initial[section.name] = fields;
					}

					return section;
				})
			};
		});

		setInitialValues(initial);
		setStepLabels(labels);
		setLoading(false);
		setSteps([...mappedSteps]);
		setPaymentInformation(payment);
		setSectionInterface(multiInterface);
	};

	const setUnit = listing => {
		const fromQuery = startedApplicationID || null;

		if (listing && isEmpty(selectedUnit)) {
			setLoading(true);
			let fetchUnitID = listing;

			if (typeof listing == 'object') {
				const { unit, property } = listing;

				fetchUnitID = unit.unitID;

				setSelectedCountryID(property.countryID);
			}

			props.endpoints
				.fetchUnit(fetchUnitID, fromQuery)
				.then(response => {
					if (response.data.pendingMessage) {
						setPendingMessage(response.data.pendingMessage);
						pendingMessageModal.toggle();
					}

					setHasPendingApplication(response.data.hasPendingApplication);

					setSelectedUnit(response.data.unit);
					setTemplateID(response.data.template.id);
					setAddenda(response.data.template.addenda);
					processSteps(
						{
							steps: response.data.template.steps,
							payment: response.data.template.payment
						},
						response.data,
						response.data.initialData
					);
					setLoading(false);
				})
				.catch(error => {
					toast.error(error.response?.data?.error);
				});
		}
	};

	const handleSubmit = (values, form) => {
		let data = {};

		// Submitted from public app
		if (props.steps) {
			data = {
				templateID,
				unitID: !isEmpty(selectedUnit) ? selectedUnit.unitID : values.unitID,
				application: {
					...values
				},
				payment: {
					address: values[APPLYING_FOR].address,
					amount: paymentInformation.totalPaymentAmount,
					cardCode: values.cvc,
					cardNumber: values.cardNumber?.replace(/\s+/g, ''),
					city: values[APPLYING_FOR].city,
					expirationMonth: values.expiryDate?.split(' / ')[0],
					expirationYear: `20${values.expiryDate?.split(' / ')[1]}`,
					nameOnCard: values.nameOnCard,
					postalCode: values[APPLYING_FOR].postalCode,
					processingFee: paymentInformation.processingFeeAmount,
					stateID: values[APPLYING_FOR].stateID
				},
				signature,
				addenda: [],
				startedApplicationUUID: urlUUID
			};

			addenda.forEach(addendum => {
				data.addenda.push({
					templateID: addendum.envelope.id,
					systemAddendumTypeID: addendum.envelope.systemAddendumTypeID
				});
			});

			delete data['cvc'];
			delete data['expiryDate'];
			delete data['nameOnCard'];
			delete data['cardNumber'];

			// Submitted from Manager portal
		} else if (props.templateID) {
			let applyingFor;

			// If unit is selected, use as applying for address
			if (values.unitID?.obj) {
				const {
					unit: { address, address2, city, stateID, postalCode },
					property: { countryID }
				} = values.unitID.obj;

				applyingFor = {
					address,
					address2,
					city,
					stateID,
					postalCode,
					countryID
				};
				// Else use address form fields, i.e. manually entered address
			} else {
				applyingFor = values.applyingFor;
			}

			data = {
				application: {
					...values,
					applyingFor
				},
				templateID: props.templateID,
				unitID: values.unitID?.value
			};

			delete data.application.unitID;
		} else {
			data = { ...values };
		}

		props.form
			.handlePromise(form, [props.id ? updateApplicant(applicantID, data) : submitApplication(data)], {
				successMessage: 'Successfully saved application'
			})
			.then(response => {
				if (props.steps) {
					setShowSuccess(true);
					setEditing(false);
					setApplicant(response.data);
				} else if (props.templateID) {
					history.push('/screening/applications');
				} else {
					setEditing(false);
					getApp();
				}
			})
			.catch(error => {
				props.notify({ error });
				form.setSubmitting?.(false);
				form.setProcessing?.(false);
			});
	};

	const advanceStep = form => {
		if (previewTemplateID) {
			setCurrentStep(currentStep + 1);
		} else {
			const step = steps[currentStep];

			if (currentStep >= steps.length || !step || !step.id) return;

			if (step.id == ApplicationSteps.Addenda) {
				const addendaErrors = {};

				addenda.forEach((addendum, addendumIndex) => {
					if (addendum.isComplete != true) {
						addendaErrors[`addendum_${addendumIndex}`] = 'Addendum needs to be signed';
					}
				});

				form.setErrors(addendaErrors);

				if (Object.keys(addendaErrors).length > 0) return;

				setCurrentStep(currentStep + 1);

				return;
			}

			setValidating(true);

			const data = {
				templateID,
				application: {
					...form.values
				}
			};

			props.form
				.handlePromise(form, [updateStartedApplication(urlUUID, step.id, data)])
				.then(() => {
					setAppliedToAddress({
						address: data.application?.[APPLYING_FOR].address,
						address2: data.application?.[APPLYING_FOR].address2,
						city: data.application?.[APPLYING_FOR].city,
						stateID: data.application?.[APPLYING_FOR].stateID,
						postalCode: data.application?.[APPLYING_FOR].postalCode,
						countryID: data.application?.[APPLYING_FOR].countryID
					});
					document.getElementById('application')?.scrollIntoView();
					setCurrentStep(currentStep + 1);
					setValidating(false);
				})
				.catch(error => {
					props.notify({ error });
					setValidating(false);
				});
		}
	};

	const changeUnit = () => {
		// Resets some state that is tracked differently than just Formik values
		toggleChange(false);
		setSelectedUnit({});
		setCurrentStep(0);
		setFileIDs([]);
		setFilePreviews([]);
		setFileNames([]);
		setUrlUUID(null);
	};

	const saveSocial = (values, form) => {
		props.form
			.handlePromise(form, [submitSocial(routeApplicantID, values)])
			.then(() => {
				setSocialEdit(false);
				setSsn(values.ssn);
				props.notify({ success: 'Successfully updated SSN' });
			})
			.catch(error => props.notify(error));
	};

	const getSocial = () => {
		if (!ssn) {
			setSsnLoading(true);

			fetchSocial(routeApplicantID).then(response => {
				setSsn(response.data.ssn);
				setSsnLoading(false);
			});
		} else {
			setSocialEdit(true);
		}
	};

	const ChangeConfirm = () => (
		<PromptModal
			isOpen={changingUnit}
			onClose={() => toggleChange(false)}
			header="Verify Unit Change"
			body="Are you sure you want to change the unit you are applying for?"
			buttons={() => (
				<>
					<Button color="success" onClick={changeUnit}>
						Change Unit
					</Button>
					<Button onClick={() => toggleChange(false)}>Cancel</Button>
				</>
			)}
		/>
	);

	const SocialForm = () => (
		<FormPanel
			header="Update SSN"
			enableReinitialize
			initialValues={{
				ssn
			}}
			isOpen={isEditingSocial}
			onSubmit={saveSocial}
			onClose={setSocialEdit}
			render={() => <TextField id="ssn" name="ssn" label="SSN" />}
		/>
	);

	const renderFieldArray = (form, section, arrayHelper) => {
		if (arrayHelper.form.values[section.name] && arrayHelper.form.values[section.name].items) {
			return arrayHelper.form.values[section.name].items.map((__, itemIndex) => (
				<div key={`${section.id}_${itemIndex}`}>
					<Row>
						<Col md="11">
							{section.groups.map((group, groupIndex) => (
								<Row key={groupIndex}>
									<div>{group.label}</div>

									{group.fields.map((field, fieldIndex) => {
										const getInField = `[${section.name}].items`;
										const countryID =
											getIn(form.values[section.name].items[itemIndex], 'countryID') || getIn(form.values[section.name].items[itemIndex], 'primaryCountryID');
										const isCurrentEmployer = getIn(form.values[section.name].items[itemIndex], 'isCurrentEmployer');
										// const birthDate = getIn(form.values[section.name].items[itemIndex], 'birthDate');

										// Hide endDate field if isCurrentEmployer is selected
										if (field.name == 'endDate' && isCurrentEmployer == 'Yes') return null;

										return (
											<Col key={fieldIndex} md={isMobile ? '12' : ''}>
												<ApplicationEntryField
													index={itemIndex}
													field={field}
													countryID={countryID}
													components={props.components}
													helperText={field.helperText}
													id={`${getInField}[${itemIndex}].${field.name}`}
													name={`${getInField}[${itemIndex}].${field.name}`}
													readOnly={!isEditing || field.readOnly}
													value={getIn(form.values[section.name].items[itemIndex], field.name)}
													onBlurRaw={value => {
														if (section.name == 'personalInfo' && field.name == 'birthDate' && getAge(value) < 18) {
															setConfirmModal({
																isOpen: true,
																birthDate: value,
																form,
																fieldName: `${section.name}.items[${itemIndex}].birthDate`
															});
														}
													}}
													onChange={(value, _, __, isCalendarOpen) => {
														// Reset endDate if isCurrentEmployer is selected
														if (field.name == 'isCurrentEmployer' && isCurrentEmployer == 'Yes') {
															form.setFieldValue(`employmentHistory[${itemIndex}].endDate`, null);
														}
														if (isCalendarOpen && section.name == 'personalInfo' && field.name == 'birthDate' && getAge(value) < 18) {
															setConfirmModal({
																isOpen: true,
																birthDate: value,
																form,
																fieldName: `${section.name}.items[${itemIndex}].birthDate`
															});
														}
													}}
												/>
											</Col>
										);
									})}
								</Row>
							))}
						</Col>
						{isEditing && (
							<Col md="1" className="field-container">
								{itemIndex + 1 > parseInt(section.requiredCount) ? (
									<div className="form-field">
										<Button onClick={() => removeGroup(arrayHelper, itemIndex)}>&minus;</Button>
									</div>
								) : (
									<Col md="1" />
								)}
							</Col>
						)}
					</Row>
					<hr />
				</div>
			));
		}
	};

	const addGroup = (arrayHelper, section) => {
		arrayHelper.push(sectionInterface[section.name][0]);
	};

	const removeGroup = (arrayHelper, sectionIndex) => {
		arrayHelper.remove(sectionIndex);
	};

	const MultiSection = ({ form, section, sectionIndex, getInField }) => {
		if (isEmpty(section.groups)) return null;
		const values = getIn(form.values, getInField);

		return (
			<FieldArray
				key={`${section.id}_${sectionIndex}`}
				name={`${getInField}`}
				render={arrayHelper => (
					<Card title={section.label}>
						<FroalaEditorView model={section.message} />
						{renderFieldArray(form, section, arrayHelper)}
						{isEmpty(values) && <div className="card__copy">{section.emptyMessage}</div>}
						{isEditing && (
							<div className="card__actions">
								<Button onClick={() => addGroup(arrayHelper, section)} color="action" size="sm">
									{section.addButtonLabel}
								</Button>
							</div>
						)}
					</Card>
				)}
			/>
		);
	};

	const SingleSection = ({ form, section, sectionIndex }) => (
		<Card title={section.label} key={sectionIndex}>
			<FroalaEditorView model={section.message} />

			{section.id == ApplicationSections.Terms && (
				<div className="card__terms">
					<em>Please acknowledge the terms below.</em>
				</div>
			)}

			{section.groups.map((group, groupIndex) => {
				return (
					<Row key={groupIndex}>
						<div>{group.label}</div>

						{group.fields.map((field, fieldIndex) => {
							if (field.isActive == '0') return null;

							const getInField = `[${section.name}]`;
							const countryID = getIn(form.values[section.name], 'countryID') || getIn(form.values[section.name], 'primaryCountryID');
							// const birthDate = getIn(form.values[section.name], 'birthDate');
							let path = `${getInField}[${field.name}]`;

							if (field.fieldTypeID == ApplicationFieldTypes.Question) {
								path = getInField;
							} else if (field.fieldTypeID == ApplicationFieldTypes.Term) {
								path = getInField;
							}

							return (
								<Col key={fieldIndex} md={isMobile ? '12' : ''}>
									<ApplicationEntryField
										components={props.components}
										countryID={countryID}
										editSocial={getSocial}
										field={field}
										helperText={field.helperText}
										id={path}
										index={groupIndex}
										name={path}
										readOnly={
											(section.id == ApplicationSections.ApplyingFor && !props.templateID && props.portalType == PortalTypes.Manager) ||
											!isEditing ||
											field.readOnly
										}
										ssn={ssn}
										ssnLoading={ssnLoading}
										isGeneric={generic}
										allowSSN={props.allowSSN}
										steps={props.steps}
										templateID={props.templateID}
										value={getIn(form.values, path)}
										onBlurRaw={value => {
											if (section.name == 'personalInfo' && field.name == 'birthDate' && getAge(value) < 18)
												setConfirmModal({
													isOpen: true,
													birthDate: value,
													form,
													fieldName: `${section.name}['birthDate']`
												});
										}}
										onChange={(value, _, __, isCalendarOpen) => {
											if (isCalendarOpen && section.name == 'personalInfo' && field.name == 'birthDate' && getAge(value) < 18) {
												setConfirmModal({
													isOpen: true,
													birthDate: value,
													form,
													fieldName: `${section.name}['birthDate']`
												});
											}
										}}
									/>
								</Col>
							);
						})}
					</Row>
				);
			})}
		</Card>
	);

	const Documents = ({ form, section, sectionIndex }) => {
		const onAttachFile = files => {
			setFileProcessing(true);

			dropHandler(
				files,
				{
					formObject: form,
					filePreviews,
					fileIDs,
					fileNames,
					config,
					callback: ids => {
						setFileProcessing(false);
						form.setFieldValue(section.name, [...ids.map(id => ({ fileID: id }))]);
					},
					onUploadFile,
					setFileNames,
					setFilePreviews,
					setFileIDs,
					notify: props.notify
				},
				toastError
			);
		};

		const handleDelete = (file, index) => {
			setFileProcessing(true);

			deleteFileHandler(file, index, {
				fileNames,
				fileIDs,
				filePreviews,
				callback: ids => {
					setFileProcessing(false);
					form.setFieldValue(
						section.name,
						ids.map(id => ({ fileID: id }))
					);
				},
				onDelete: props.endpoints.deleteFile ? onDelete : null,
				setFileNames,
				setFileIDs,
				setFilePreviews
			});
		};
		let errors = getIn(form.errors, section.name);

		if (!Array.isArray(errors)) {
			errors = [errors];
		}

		return (
			<div key={sectionIndex}>
				<FroalaEditorView model={section.message} />

				<Dropzone onDrop={onAttachFile} validator={fileSizeValidator} className="image-uploader">
					{({ getRootProps, getInputProps }) => (
						<div {...getRootProps({ className: 'dropzone' })} className="image-uploader">
							Drag files here to upload
							<br />
							(or click to open dialog)
							<input {...getInputProps()} />
						</div>
					)}
				</Dropzone>
				<div className="form-field-error">
					{getErrorMessages(errors).flatMap((error, index) => (
						<div key={index}>{error}</div>
					))}
				</div>
				<ShowFiles filePreviews={filePreviews} handleDelete={handleDelete} />
				{!props.steps && form.values.documents && (
					<ul className="file-list">
						{form.values.documents.map((document, index) => (
							<li key={index} className="file-list__item">
								<div className="file-list__item--second">
									<RenderIcon file={document} />
									<Link to={`/api/manager/files/${document.fileID}/preview`} target="_blank">
										<div className="link-color file-list__item-link">{document.title}</div>
									</Link>
								</div>
							</li>
						))}
					</ul>
				)}
			</div>
		);
	};

	const renderAddendum = form => {
		if (!addendum) return null;

		return (
			<AddendumModal
				form={form}
				envelope={addendum.envelope}
				toggle={() => {
					setAddendum(null);
				}}
				signature={signature}
				promptSignature={toggleSignature}
				onSubmit={() => {
					const newAddenda = update(addenda, {
						$apply: list => {
							const newList = [];

							list.forEach(item => {
								const newItem = { ...item };

								if (
									(item.envelope.id && item.envelope.id == addendum.envelope.id) ||
									(item.envelope.systemAddendumTypeID && item.envelope.systemAddendumTypeID == addendum.envelope.systemAddendumTypeID)
								) {
									newItem.isComplete = true;
								}

								newList.push(newItem);
							});

							return newList;
						}
					});
					setAddenda(newAddenda);
					setAddendum(null);
				}}
			/>
		);
	};

	const isSectionInactive = section => !props.steps && (section.isActive == '0' || !section.isActive);

	const Addenda = ({ form, section, sectionIndex }) => {
		return (
			<div key={sectionIndex}>
				<FroalaEditorView model={section.message} />
				{addenda && addenda.length > 0 ? (
					<>
						<ul className="addenda-list">
							{addenda.map((addendum, addendumIndex) => {
								const errorMessage = form.errors ? form.errors[`addendum_${addendumIndex}`] : null;

								return (
									<li key={addendumIndex} className="addenda-list__item">
										<Row className="addenda-list__row">
											<Col md="8">{addendum.envelope.name}</Col>
											<Col md="4">
												<div className="text-md-right">
													{addendum.isComplete ? (
														<div className="text-success">
															<i className="fal fa-check"></i> Complete
														</div>
													) : (
														<Button
															size="sm"
															color="warning"
															onClick={() => {
																setAddendum(addendum);
															}}>
															Review and Sign
														</Button>
													)}
												</div>
											</Col>
										</Row>
										{errorMessage ? <div className="text-danger">{errorMessage}</div> : null}
									</li>
								);
							})}
						</ul>
						{renderAddendum(form)}
					</>
				) : null}
			</div>
		);
	};

	const SignaturePrompt = () => {
		if (!promptSignature) return null;

		return (
			<Modal isOpen={true} zIndex={2000}>
				<Form
					initialValues={{ ...signature }}
					validate={values => {
						const errors = {};

						if (isEmpty(values.fullName)) {
							errors.fullName = 'Full Name is Required';
						}

						if (isEmpty(values.initials)) {
							errors.initials = 'Initials Required';
						}

						return errors;
					}}
					onSubmit={values => {
						setSignature(values);
						setPromptSignature(false);
					}}
					render={form => {
						return (
							<>
								<ModalHeader>Confirm Signature</ModalHeader>
								<ModalBody>
									<Row className="form-row">
										<Col md="8">
											<TextField name="fullName" id="fullName" label="Full Name" />
										</Col>
										<Col md="4">
											<TextField name="initials" id="initials" label="Initials" maxLength="5" />
										</Col>
									</Row>
									<Row className="form-row">
										<Col md="8">
											{!isEmpty(form.values.fullName) ? (
												<img
													src={`/api/public/screening/addendums/signature?signature=${form.values.fullName}`}
													alt="Signature"
													className="form-row__image"
												/>
											) : null}
										</Col>
										<Col md="4">
											{!isEmpty(form.values.initials) ? (
												<img src={`/api/public/screening/addendums/initials?initials=${form.values.initials}`} alt="Initials" className="form-row__image" />
											) : null}
										</Col>
									</Row>
								</ModalBody>
								<ModalFooter>
									<Button
										onClick={() => {
											form.handleSubmit();
										}}
										color="primary">
										Confirm
									</Button>
									{'  '}
									<Button onClick={toggleSignature} outline>
										Cancel
									</Button>
								</ModalFooter>
							</>
						);
					}}
				/>
			</Modal>
		);
	};

	/**
	 * Used for PM submitted applications to allow them to submit either unit selection or
	 * manually entered address
	 */
	const UnitFieldToggle = ({ form }) => {
		const [toggleVal, setToggleVal] = useState('unit');

		const handleSelect = (_, newVal) => {
			// Unset unitID if toggle value is changed to make sure it's not used accidentally, e.g. select unit then enter address manually
			form.setFieldValue('unitID', null);
			setToggleVal(newVal);
		};

		const StateField = props.components.StateField;
		const CountryField = props.components.CountryField;

		return (
			<>
				<ToggleButtonGroup value={toggleVal} exclusive onChange={handleSelect} size="small">
					<ToggleButton value="unit">Unit Selection</ToggleButton>
					<ToggleButton value="address">Address Entry</ToggleButton>
				</ToggleButtonGroup>
				{toggleVal == 'unit' ? (
					<UnitField id="unitID" name="unitID" label="Unit" fullObj filter={{ includes: 'property' }} isRequired />
				) : (
					<Row>
						<Col>
							<Row>
								<Col lg="6">
									<TextField id="applyingFor.address" name="applyingFor.address" label="Address" />
								</Col>
								<Col lg="6">
									<TextField id="applyingFor.address2" name="applyingFor.address2" label="Unit/Apt #" />
								</Col>
							</Row>
							<Row>
								<Col lg="4">
									<TextField id="applyingFor.city" name="applyingFor.city" label="City" />
								</Col>
								<Col lg="4">
									<StateField id="applyingFor.stateID" name="applyingFor.stateID" label="State" />
								</Col>
								<Col lg="4">
									<TextField id="applyingFor.postalCode" name="applyingFor.postalCode" label="Postal Code" />
								</Col>
							</Row>
							<Row>
								<Col lg="4">
									<CountryField id="applyingFor.countryID" name="applyingFor.countryID" label="Country" />
								</Col>
							</Row>
						</Col>
					</Row>
				)}
			</>
		);
	};

	const ApplicationForm = ({ form }) => {
		if (props.steps) {
			if (steps.length < 1) return null;

			if (steps[currentStep].id == ApplicationSteps.ReviewSign) {
				return (
					<Card title="Review & Sign">
						<FroalaEditorView model={reviewMessage} />
						<TextField className="signature" id="reviewSign.signature" name="reviewSign.signature" label="Signature" />
					</Card>
				);
			} else if (steps[currentStep].id == ApplicationSteps.Pay) {
				return paymentInformation?.totalPaymentAmount == 0 ? (
					<SubmitOnly setSubmit={setSubmit} />
				) : (
					<Submit paymentInformation={paymentInformation} paymentMessage={paymentMessage} setSubmit={setSubmit} />
				);
			}

			return (
				<>
					<div className="application-label">
						<Stack direction="row" justifyContent="space-between">
							{steps[currentStep].label}
							<p style={{ margin: 0 }}>
								Step <strong>{currentStep + 1}</strong>/<strong>{steps && steps.length}</strong>
							</p>
						</Stack>
					</div>
					{confirmModal.isOpen && (
						<ConfirmDateModal
							isOpen={true}
							header="Confirm Date of Birth"
							body={`The date you entered is under the age of 18. Are you sure ${confirmModal.birthDate} is your correct Date of Birth?`}
							onClose={() => {
								setConfirmModal({ ...confirmModal, isOpen: false });
								form.setFieldValue(confirmModal.fieldName, null);
							}}
							onConfirm={() => setConfirmModal({ ...confirmModal, isOpen: false })}
						/>
					)}

					{steps[currentStep].sections?.map((section, sectionIndex) => {
						const getInField = `${section.name}.items`;

						return section.id == ApplicationSections.Addenda ? (
							<Addenda form={form} section={section} sectionIndex={sectionIndex} key={sectionIndex} />
						) : section.id == ApplicationSections.IncomeVerification ? (
							<IncomeVerification
								providerToken={providerLinkToken}
								completed={incomeVerificationCompleted}
								setToken={handleIncomeVerificationToken}
								message={section.message}
								key={sectionIndex}
							/>
						) : section.id == ApplicationSections.IncomeVerificationDocuments || section.id == ApplicationSections.IdentityVerificationDocuments ? (
							<VerificationDocuments section={section} onUploadFile={onUploadFile} onDelete={onDelete} key={sectionIndex} />
						) : section.id == ApplicationSections.IdentityVerification ? (
							<IdentityVerification
								providerToken={identityProviderToken}
								completed={identityVerificationCompleted}
								onComplete={() => setIdentityVerificationCompleted(true)}
								key={sectionIndex}
								message={section.message}
							/>
						) : section.id == ApplicationSections.Documents ? (
							<Documents form={form} section={section} sectionIndex={sectionIndex} key={sectionIndex} />
						) : section.isMulti == true ? (
							<MultiSection form={form} getInField={getInField} section={section} sectionIndex={sectionIndex} key={sectionIndex} />
						) : (
							<SingleSection form={form} section={section} sectionIndex={sectionIndex} key={sectionIndex} />
						);
					})}
				</>
			);
		}

		return (
			<Col md="12">
				{steps.map(
					(step, stepIndex) =>
						step.includedForView == 1 && (
							<Fragment key={stepIndex}>
								<div className="application-label">{step.label}</div>
								{step.sections?.map((section, sectionIndex) => {
									if (isSectionInactive(section)) return <Fragment key={sectionIndex} />;
									else if (props.templateID && section.id == ApplicationSections.ApplyingFor) {
										return (
											<Card key={sectionIndex} title="Applying For">
												<UnitFieldToggle form={form} />
											</Card>
										);
									}

									const getInField = `${section.name}.items`;

									return (
										<Fragment key={`${sectionIndex}_${section.id}`}>
											{section.id == ApplicationSections.Documents ? (
												props.steps && <Documents form={form} section={section} sectionIndex={sectionIndex} />
											) : section.isMulti == true ? (
												<MultiSection form={form} getInField={getInField} section={section} sectionIndex={sectionIndex} />
											) : (
												<SingleSection form={form} section={section} sectionIndex={sectionIndex} />
											)}
										</Fragment>
									);
								})}
							</Fragment>
						)
				)}
			</Col>
		);
	};

	const renderStepButtons = form => {
		const isDisabled = (props.steps && !previewTemplateID && !urlUUID && isEmpty(selectedUnit)) || isFileProcessing || isValidating;

		return (
			<>
				{generic && currentStep == 0 ? (
					<Button
						onClick={() => {
							history.push('/apply');
							changeUnit();
						}}>
						Cancel
					</Button>
				) : (
					<Button onClick={() => setCurrentStep(currentStep - 1)} disabled={currentStep == 0}>
						Previous
					</Button>
				)}
				{'   '}
				{steps[currentStep]?.id != ApplicationSteps.Pay && (
					<Button color="primary" disabled={isDisabled} onClick={() => advanceStep(form)}>
						{isValidating ? (
							<>
								<i className="fal fa-circle-notch fa-spin" /> Continue
							</>
						) : (
							'Continue'
						)}
					</Button>
				)}
			</>
		);
	};

	const ApplicationButtons = ({ form }) => (
		<>
			<li>
				<Button color="primary" disabled={form.isSubmitting} onClick={form.submitForm}>
					{form.isSubmitting ? 'Saving...' : 'Save'}
				</Button>
			</li>
			<li>
				<Button type="button" disabled={form.isSubmitting} onClick={history.goBack}>
					Cancel
				</Button>
			</li>
		</>
	);

	const isTouchDevice = () => {
		if (window.screen.width < 768) {
			setIsMobile(true);

			return;
		} else {
			setIsMobile(false);
		}
	};

	const renderPendingMessage = () => {
		if (!pendingMessage) return null;

		return pendingMessageModal.render({
			header: <span className="pending-message-modal-header">Important Message</span>,
			body: <FroalaEditorView model={pendingMessage} />,
			buttons: () => (
				<Button color="secondary" onClick={() => pendingMessageModal.toggle()}>
					Close
				</Button>
			)
		});
	};

	const toggleAuditLog = () => setShowAuditLog(prevState => !prevState);

	const PreviousApplication = () => (
		<Box mx="auto">
			<Card
				title="You already have an application in progress"
				renderFooter={() => (
					<Button
						color="primary"
						onClick={() => {
							setUrlUUID(previousApplication);
							history.replace(`/apply?application=${previousApplication}`);
							setPreviousApplication(null);
						}}>
						Continue Previous Application
					</Button>
				)}>
				<Typography mb={6}>
					Continue your previous application, or <Link to="/">cancel the application</Link> if you want to start a new one.
				</Typography>
			</Card>
		</Box>
	);

	return (
		<FilesProvider objectID={routeApplicantID} objectTypeID={ObjectTypes.Applicant}>
			<Page
				style={{
					body: 'margin: 0'
				}}
				// Want to hide the header when applicant is filling out an application, but not when component
				// is used to view applicant details in the manager portal
				hideHeader={!props.templateID && !props.steps ? false : true}
				subTitle={() =>
					!props.templateID &&
					!props.steps && (
						<PageNav
							styleoverride={{ fontSize: '13px', marginBottom: '-21px' }}
							current={tab}
							options={[
								{
									label: !isEmpty(applicant) ? `${applicant?.firstName} ${applicant?.lastName}` : 'Applicant',
									value: ApplicantTabs.Applicant,
									onClick: () => {
										setTab(ApplicantTabs.Applicant);
									}
								},
								{
									label: 'Files',
									value: ApplicantTabs.Files,
									onClick: () => {
										setTab(ApplicantTabs.Files);
									}
								},
								{
									label: 'Addenda',
									value: ApplicantTabs.Addenda,
									onClick: () => {
										setTab(ApplicantTabs.Addenda);
									}
								},
								{
									label: 'Income',
									value: ApplicantTabs.Income,
									onClick: () => {
										setTab(ApplicantTabs.Income);
									}
								},
								{
									label: 'Identity',
									value: ApplicantTabs.Identity,
									onClick: () => {
										setTab(ApplicantTabs.Identity);
									}
								},
								{
									label: 'Landlord Verification',
									value: ApplicantTabs.Landlord,
									onClick: () => {
										setTab(ApplicantTabs.Landlord);
									}
								}
							]}
						/>
					)
				}
				actions={() => {
					if (tab != ApplicantTabs.Applicant || isEmpty(applicant) || showSuccess) return;

					const actions = [
						{
							label: 'Print',
							external: true,
							to: `/api/manager/screening/applicants/${routeApplicantID}/print`
						},
						{
							label: 'Audit Log',
							onClick: toggleAuditLog
						},
						{
							label: 'Back to Application',
							to: `/screening/applications/${props.id}`
						}
					];

					if (props.allowEdit) {
						actions.unshift({
							label: 'Edit',
							onClick: () => setEditing(true)
						});
					}

					return isEditing
						? [
								{
									label: 'Editing',
									onClick: () => setEditing(false)
								}
							]
						: actions;
				}}
				breadcrumbs={props.breadcrumbs}
				hideBreadcrumbs={props.breadcrumbs ? false : true}
				files={tab === ApplicantTabs.Files}
				hasList={tab === ApplicantTabs.Files}>
				{() => {
					return showSuccess ? (
						<ApplicationSuccess
							applicant={applicant}
							address={appliedToAddress}
							getApplicationGroup={props.endpoints.getApplicationGroup}
							createApplicationGroup={props.endpoints.createApplicationGroup}
							handlePromise={props.form.handlePromise}
							notify={props.notify}
						/>
					) : (
						<div ref={scroller} id="application" className="application" style={{ height: 'inherit' }}>
							<Form
								enableReinitialize
								initialValues={(startedApplicationID || !props.steps) && !isEmpty(applicationInformation) ? applicationInformation : initialValues}
								onSubmit={handleSubmit}
								render={form => (
									<>
										{/* Not sure how to handle the height here. Files tab requires the full height to be able to expand and fill the
                                            full space but doing this will mess up the placement of the footer buttons. Needs to be reworked but
                                            dynamically applying style if in files tab for now. */}
										<Row style={{ height: tab === ApplicantTabs.Files ? 'inherit' : null, margin: 0 }}>
											{isSelectingListing && Listings ? (
												<Col>
													<Listings onSelect={setUnit} />
												</Col>
											) : previousApplication ? (
												<PreviousApplication />
											) : props.steps ? (
												<>
													<Col md="9" className="relative-column">
														<div style={{ position: 'sticky', top: 0, zIndex: 100 }}>
															<PreviewingAlert show={preview} />
														</div>
														{!isEmpty(selectedUnit) && props.steps && !showSuccess && (
															<Box display="flex" justifyContent="space-between">
																<Box mb={3}>
																	<dt style={{ fontSize: '.8rem' }}>Applying For</dt>
																	<Address value={selectedUnit} displayInline showCountry={false} />
																</Box>
																<Box mb={3}>
																	<dt style={{ fontSize: '.8rem' }}>Property Management Company</dt>
																	<Text value={company.name} />
																</Box>
															</Box>
														)}
														{hasPendingApplication && (
															<Alert severity="warning" sx={{ marginBottom: 3 }}>
																<Typography mr={2} component="span">
																	This unit has a pending application
																</Typography>
																{pendingMessage && (
																	<Button color="secondary" size="sm" onClick={() => pendingMessageModal.toggle()}>
																		More Details
																	</Button>
																)}
															</Alert>
														)}
														<ApplicationForm form={form} />
													</Col>
													{!isEmpty(urlUUID) || !isEmpty(selectedUnit) || !isEmpty(applicationInformation) || (previewTemplateID && isMobile == false) ? (
														<Col md="3" className="d-none d-md-block">
															<div className="application-progress">
																{steps.map(({ label }, index) => (
																	<div
																		key={index}
																		className="step"
																		style={{
																			display: 'flex',
																			alignItems: 'baseline',
																			justifyContent: 'space-between'
																		}}>
																		<p className={currentStep == index ? 'active' : ''} style={{ margin: '0' }}>
																			{label}
																		</p>
																		{currentStep > index && (
																			<div style={{ padding: '0 10px' }}>
																				<i className="fal fa-check" />
																			</div>
																		)}
																	</div>
																))}
															</div>
															<br />
														</Col>
													) : null}
													<Pay
														isViewing={previewTemplateID}
														isPreviewing={preview == 'true'}
														handleSubmit={handleSubmit}
														showSubmitModal={showSubmitModal}
														setSubmit={setSubmit}
														values={form.values}
													/>
												</>
											) : (
												<Col md="12" style={tab === ApplicantTabs.Files ? { display: 'contents' } : {}}>
													<TabContent
														activeTab={tab}
														style={{
															height: '100%',
															width: '100%',
															padding: tab === ApplicantTabs.Files ? '0 16px' : '0'
														}}>
														<TabPane tabId={ApplicantTabs.Applicant}>
															<ApplicationForm form={form} />
														</TabPane>
														<TabPane tabId={ApplicantTabs.Files} style={{ height: 'inherit' }}>
															<Box display="flex" flexDirection="column" sx={{ height: '100%', pb: { xs: 9, sm: 4 } }}>
																<FilesTable />
															</Box>
														</TabPane>
														<TabPane tabId={ApplicantTabs.Addenda}>
															<Card title="Addenda">
																{addenda.length == 0 ? (
																	<div className="text-center">No Addenda have been submitted</div>
																) : (
																	<ul>
																		{addenda.map((addendum, index) => {
																			const url = `/api/manager/screening/applicants/addenda/${addendum.applicantAddendumID}/preview`;

																			return (
																				<li key={index}>
																					<Row>
																						<Col>
																							<a href={url} target="_blank" rel="noreferrer">
																								{addendum.title}
																							</a>
																						</Col>
																						<Col>
																							<div className="text-right">
																								<Button size="sm" href={url} target="_blank" outline>
																									<i className="fal fa-download"></i>
																									<span className="sr-only">Download</span>
																								</Button>
																							</div>
																						</Col>
																					</Row>
																				</li>
																			);
																		})}
																	</ul>
																)}
															</Card>
														</TabPane>
														<TabPane tabId={ApplicantTabs.Income}>
															<TransactionsPage incomeTransactions={incomeTransactions} />
														</TabPane>
														<TabPane tabId={ApplicantTabs.Identity}>
															<IdentityPage verificationData={identityVerificationData} onResubmit={handleIdentityVerificationResubmitted} />
														</TabPane>
														<TabPane tabId={ApplicantTabs.Landlord}>
															{applicant.applicantID && (
																<LandlordVerificationPage
																	applicantID={applicant.applicantID}
																	getVerifications={getLandlordVerifications}
																	sendVerification={sendLandlordVerification}
																/>
															)}
														</TabPane>
													</TabContent>
												</Col>
											)}
										</Row>
										{preview && <div className="clear" />}
										{(props.publicApp || isEditing) && !isSelectingListing && !previousApplication ? (
											// Workaround to prevent the iOS and Android search bar from hiding bottom content. Should use something like
											// 100dvh on a parent container, but the PaperContainer component is currently using 100vh so will
											// override it.
											// Update - just adding padding to bottom for all devices since tablets are affected too.
											<Row style={{ margin: 0, paddingBottom: '128px' }}>
												<Col md={props.publicApp ? '9' : '12'} style={{ padding: 0 }}>
													<ApplicationsContext.Provider value={{ onDeleteApplication }}>
														<Stack direction="row" justifyContent="flex-end" px="14px" pb={4}>
															<ul className="list-buttons">
																{allowCancel ? (
																	<CancelButton id={originalStartedApplicationID} />
																) : props.steps ? (
																	renderStepButtons(form)
																) : isEditing ? (
																	<ApplicationButtons form={form} />
																) : null}
															</ul>
														</Stack>
													</ApplicationsContext.Provider>
												</Col>
											</Row>
										) : null}
									</>
								)}
							/>
							<SocialForm />
							<ChangeConfirm />
							<SignaturePrompt />
							{!isEmpty(RenderAuditLogModal) && <RenderAuditLogModal showAuditLog={showAuditLog} toggleAuditLog={toggleAuditLog} applicantID={applicantID} />}

							{renderPendingMessage()}
						</div>
					);
				}}
			</Page>
		</FilesProvider>
	);
}

export default withForm(Application);
